import { getUserDetails, makeGetCall, makePostCall } from './helper';

/**
 * Where the reporting editor and the DICOM viewer land on a dual-monitor desk.
 *
 * 'viewer-other'    (default — what everyone got before this preference existed):
 *                   reporting opens as a TAB in the current browser window, so it stays
 *                   on the monitor the radiologist is already looking at, and the viewer
 *                   pops up on the other monitor inset to 95% of that screen.
 *
 * 'reporting-other' the mirror image, for radiologists who would rather keep images in
 *                   front of them: reporting opens as its own WINDOW on the other
 *                   monitor, and the viewer fills the monitor they are sitting at.
 *
 * A viewer launched from inside the reporting page needs no special-casing: MonitorManager
 * places it on the screen its opener is NOT on — so moving reporting to the other monitor
 * sends the viewer back to the original one for free.
 *
 * A viewer launched straight from a study list (the "Open Viewer" tool, or the patient-name
 * click for technicians) does need it. That click comes from the monitor the user is
 * sitting at, so under 'reporting-other' the images must stay on the CURRENT screen instead
 * of jumping away from the opener — see MonitorManager.resolveViewerPlacement().
 */
export type WorkstationLayout = 'viewer-other' | 'reporting-other';

export const DEFAULT_WORKSTATION_LAYOUT: WorkstationLayout = 'viewer-other';

/**
 * How many viewer windows this user wants kept open at once, overriding the centre-wide
 * ENV_CONFIG.VIEWER_BUFFER_TABS. `null` means "follow the centre default", which is what
 * everybody gets until they change it — the same tri-state shape as the report-preview
 * preference, so a centre can retune its default without overwriting anyone's choice.
 *
 * The ceiling is deliberate: each buffer slot is a real browser window holding a decoded
 * DICOM stack, and a radiologist who sets this to 20 will run the workstation out of
 * memory long before they run out of windows to look at.
 */
export const MIN_VIEWER_BUFFER_TABS = 1;
export const MAX_VIEWER_BUFFER_TABS = 6;

export interface WorkstationPrefs {
  layout: WorkstationLayout;
  /** null = follow the centre default. */
  viewerBufferTabs: number | null;
}

export const DEFAULT_WORKSTATION_PREFS: WorkstationPrefs = {
  layout: DEFAULT_WORKSTATION_LAYOUT,
  viewerBufferTabs: null,
};

/**
 * Persisted in the generic `user_preferences` table (up_username, up_type, up_value JSONB)
 * that already backs mouse actions, favourite tools, hotkeys and panel positions — so this
 * needs no migration and no new column, just a new up_type. Stored as an object rather
 * than a bare string so further workstation settings can join it later without another
 * preference type — which is exactly what `viewerBufferTabs` did.
 */
const PREFERENCE_TYPE = 'workstation_layout';

/**
 * localStorage is ONLY a synchronous cache of the row above, never the source of truth.
 * The preference is read at click time immediately before `window.open`, where awaiting a
 * round-trip would add latency and risk the browser dropping user activation (a blocked
 * popup). Logout calls localStorage.clear(); preloadWorkstationLayout() refills it from
 * the database on the next boot, which is a full page load after every login.
 *
 * Keyed by username so that a shared workstation can never serve one radiologist's seating
 * habit to the next person who logs in.
 */
const STORAGE_KEY = 'pacs_workstation_layout';

const currentUsername = (): string => {
  try {
    const user = getUserDetails();
    return user?.username || user?.user_name || '';
  } catch {
    return '';
  }
};

const asLayout = (value: any): WorkstationLayout | null =>
  value === 'reporting-other' || value === 'viewer-other' ? value : null;

/** A usable buffer count, or null for "follow the centre default". */
export const asViewerBufferTabs = (value: any): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n)) return null;
  return Math.min(Math.max(n, MIN_VIEWER_BUFFER_TABS), MAX_VIEWER_BUFFER_TABS);
};

/**
 * Coerce whatever is in storage (or in the preference row) into a full prefs object.
 * Tolerates the bare 'viewer-other' string that the first version of this preference
 * cached, so shipping this never has to invalidate anyone's localStorage.
 */
const asPrefs = (value: any): WorkstationPrefs => ({
  layout: asLayout(typeof value === 'string' ? value : value?.layout) || DEFAULT_WORKSTATION_LAYOUT,
  viewerBufferTabs: typeof value === 'string' ? null : asViewerBufferTabs(value?.viewerBufferTabs),
});

const readCache = (): Record<string, any> => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    // Corrupt JSON, or storage blocked on a locked-down browser profile.
    return {};
  }
};

const writeCache = (username: string, prefs: WorkstationPrefs): void => {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...readCache(), [username]: prefs })
    );
  } catch (e) {
    console.warn('workstationLayout: failed to write localStorage cache', e);
  }
};

/** Synchronous read of the whole preference, safe to call immediately before window.open. */
export const getWorkstationPrefs = (): WorkstationPrefs => {
  const username = currentUsername();
  if (!username) return DEFAULT_WORKSTATION_PREFS;

  return asPrefs(readCache()[username]);
};

/** Synchronous read, safe to call immediately before window.open. */
export const getWorkstationLayout = (): WorkstationLayout => getWorkstationPrefs().layout;

/**
 * This user's viewer-window count, or null when they have not overridden the centre
 * default. Read at click time by the slot registry — never memoised, so raising the limit
 * takes effect on the very next study without a reload.
 */
export const getViewerBufferTabs = (): number | null => getWorkstationPrefs().viewerBufferTabs;

/**
 * Hydrate the cache from the database. Called from index.js before the app mounts, so the
 * first click already reads the stored value. Safe no-op when nobody is logged in.
 */
export const preloadWorkstationLayout = async (): Promise<WorkstationPrefs | null> => {
  const username = currentUsername();
  if (!username) return null;

  try {
    const resp = await makeGetCall(
      `/get-user-preference?username=${encodeURIComponent(username)}&type=${PREFERENCE_TYPE}`
    );
    const prefs = asPrefs(resp?.data?.data);

    // A successful fetch is authoritative: mirror exactly what the row holds (the defaults
    // when there is no row yet) so a preference cleared server-side cannot linger here.
    writeCache(username, prefs);
    return prefs;
  } catch (e) {
    // Offline or the endpoint failed — keep whatever the cache already holds.
    console.warn('workstationLayout: preload failed, using cached/default prefs', e);
    return null;
  }
};

/**
 * Persist the preference. Writes the cache first so the very next click honours the new
 * value even while the request is still in flight. Resolves false when the row could not
 * be saved, so the caller can tell the user it will not follow them to another machine.
 *
 * Takes a PARTIAL and merges it over what is already stored: the row holds several
 * independent settings under one up_type, so writing `{ layout }` alone would silently
 * blank out the user's viewer-window count.
 */
export const setWorkstationPrefs = async (
  partial: Partial<WorkstationPrefs>
): Promise<boolean> => {
  const username = currentUsername();
  if (!username) return false;

  const merged: WorkstationPrefs = { ...getWorkstationPrefs(), ...partial };
  writeCache(username, merged);

  try {
    await makePostCall('/save-user-preference', {
      username,
      type: PREFERENCE_TYPE,
      value: merged,
    });
    return true;
  } catch (e) {
    console.error('workstationLayout: failed to persist preference', e);
    return false;
  }
};

/**
 * True when reporting should open on the other monitor and the viewer should fill the one
 * the user is sitting at. Read at click time (never memoised), so flipping the preference
 * takes effect in every already-open tab without a reload.
 */
export const isReportingOnOtherScreen = (): boolean =>
  getWorkstationLayout() === 'reporting-other';
